import type {
  ConferenceIndex,
  StudyData,
  ThreadsIndex,
} from '../../data/schema';
import { allThreads } from '../../data/threads';
import { compactVerseSelection } from '../scriptures/sciLinks';

const HIGHLIGHT_LABELS = {
  yellow: 'Key insight',
  blue: 'Scripture or doctrine connection',
  green: 'Personal application or action item',
  pink: 'Quote to remember',
} as const;

function cleanMarkdown(value: string): string {
  return value.replaceAll(/\s+/g, ' ').trim();
}

export function buildStudyMarkdown(
  data: StudyData,
  index: ConferenceIndex,
  threads: ThreadsIndex,
  baseUrl = window.location.origin,
): string {
  const talkById = new Map(index.talks.map((talk) => [talk.talkId, talk]));
  const threadById = new Map(
    allThreads(threads).map((thread) => [thread.threadId, thread]),
  );
  const completedTalks = data.readingProgress.filter(
    (progress) => progress.completed,
  ).length;
  const lines = [
    '# My Conference Study',
    '',
    index.title,
    '',
    '## Journey',
    '',
    `- Talks completed: ${String(completedTalks)} of ${String(index.talks.length)}`,
    `- Highlights: ${String(data.highlights.length)}`,
    `- Notes: ${String(data.notes.length)}`,
    `- Bookmarks: ${String(data.bookmarks.length)}`,
    `- Threads completed: ${String(data.threadProgress.filter((progress) => progress.completed).length)}`,
    `- Scriptures explored: ${String(data.exploredScriptures.length)}`,
    '',
    '## Bookmarks',
    '',
  ];

  if (data.bookmarks.length === 0) {
    lines.push('No bookmarked talks.', '');
  } else {
    for (const bookmark of data.bookmarks) {
      const talk = talkById.get(bookmark.talkId);
      const label = talk
        ? `${talk.title} by ${talk.speaker.name}`
        : bookmark.talkId;
      lines.push(`- [${cleanMarkdown(label)}](${baseUrl}/read/${bookmark.talkId})`);
    }
    lines.push('');
  }

  lines.push('## Highlights', '');
  if (data.highlights.length === 0) {
    lines.push('No highlights.', '');
  } else {
    for (const color of Object.keys(HIGHLIGHT_LABELS) as Array<
      keyof typeof HIGHLIGHT_LABELS
    >) {
      const highlights = data.highlights.filter(
        (highlight) => highlight.color === color,
      );
      if (highlights.length === 0) continue;
      lines.push(`### ${HIGHLIGHT_LABELS[color]}`, '');
      for (const highlight of highlights) {
        const talk = talkById.get(highlight.talkId);
        const label = talk?.title ?? highlight.talkId;
        lines.push(
          `> ${cleanMarkdown(highlight.textSnippet)}`,
          '',
          `[${cleanMarkdown(label)}](${baseUrl}/read/${highlight.talkId}/${highlight.paragraphId})`,
          '',
        );
      }
    }
  }

  lines.push('## Notes', '');
  if (data.notes.length === 0) {
    lines.push('No notes.', '');
  } else {
    for (const note of data.notes) {
      const talk = talkById.get(note.talkId);
      const label = talk?.title ?? note.talkId;
      const paragraphPath = note.paragraphId ? `/${note.paragraphId}` : '';
      lines.push(
        `### [${cleanMarkdown(label)}](${baseUrl}/read/${note.talkId}${paragraphPath})`,
        '',
        note.noteText.trim(),
        '',
      );
    }
  }

  lines.push('## Reading Progress', '');
  if (data.readingProgress.length === 0) {
    lines.push('No reading progress yet.', '');
  } else {
    for (const progress of data.readingProgress) {
      const talk = talkById.get(progress.talkId);
      const label = talk?.title ?? progress.talkId;
      const paragraphPath = progress.paragraphId
        ? `/${progress.paragraphId}`
        : '';
      lines.push(
        `- [${cleanMarkdown(label)}](${baseUrl}/read/${progress.talkId}${paragraphPath}): ${progress.completed ? 'Completed' : `${String(Math.round(progress.percent))}%`}`,
      );
    }
    lines.push('');
  }

  lines.push('## Thread Progress', '');
  if (data.threadProgress.length === 0) {
    lines.push('No thread progress yet.', '');
  } else {
    for (const progress of data.threadProgress) {
      const thread = threadById.get(progress.threadId);
      lines.push(
        `- [${cleanMarkdown(thread?.title ?? progress.threadId)}](${baseUrl}/threads/${progress.threadId}): ${progress.completed ? 'Completed' : `${String(progress.completedTalkIds.length)} talks completed`}`,
      );
    }
    lines.push('');
  }

  lines.push('## Explored Scriptures', '');
  if (data.exploredScriptures.length === 0) {
    lines.push('No explored scriptures yet.', '');
  } else {
    for (const scripture of data.exploredScriptures) {
      const selection = compactVerseSelection(scripture.verses);
      const label = `${scripture.bookId} ${String(scripture.chapter)}:${selection}`;
      lines.push(
        `- [${label}](${baseUrl}/scripture/${scripture.bookId}/${String(scripture.chapter)}/${selection})`,
      );
    }
    lines.push('');
  }
  return `${lines.join('\n').trim()}\n`;
}
